import axios from 'axios'
import config from '../../config'
import { getUserToken } from '../../contexts/Auth'

const SESSION_STORAGE_KEY = 'tracking_session_id'

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getSessionId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    let sessionId = window.localStorage.getItem(SESSION_STORAGE_KEY)
    if (!sessionId) {
      sessionId = uuid()
      window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId)
    }
    return sessionId
  } catch (error) {
    return null
  }
}

interface TrackingContext {
  submissionId?: string
  flowType?: string
  step?: string
  lang?: string
}

let context: TrackingContext = {}

// Called as the wizard learns which submission/step/flow the user is on, so subsequent
// events carry that context without every call site needing to pass it explicitly.
function setContext(next: TrackingContext) {
  context = { ...context, ...next }
}

// Fire-and-forget: tracking must never throw, reject, or delay the caller. Any failure here
// (network, backend outage, malformed payload) is swallowed so it can't affect the user.
function logEvent(eventName: string, properties?: Record<string, any>) {
  try {
    const sessionId = getSessionId()
    if (!sessionId || !config?.restful?.URL) return

    const token = getUserToken()

    axios
      .post(
        `${config.restful.URL}/submission/tracking/event`,
        {
          session_id: sessionId,
          submission_id:
            context.submissionId && context.submissionId !== 'new'
              ? context.submissionId
              : undefined,
          flow_type: context.flowType,
          event_name: eventName,
          step: context.step,
          properties,
          lang: context.lang,
        },
        {
          headers: token ? { Authorization: token } : undefined,
          timeout: 5000,
        },
      )
      .catch(() => {
        // Intentionally ignored - tracking is best-effort only.
      })
  } catch (error) {
    // Intentionally ignored - tracking must never break the app.
  }
}

const InternalTracker = {
  setContext,
  logEvent,
}

export default InternalTracker
